import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@iq/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Brain } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_INSTRUCTIONS = 2000;

type Tone = 'concise' | 'balanced' | 'detailed';

const TONES: Array<{ value: Tone; labelKey: string }> = [
  { value: 'concise', labelKey: 'settings.personalization.toneConcise' },
  { value: 'balanced', labelKey: 'settings.personalization.toneBalanced' },
  { value: 'detailed', labelKey: 'settings.personalization.toneDetailed' },
];

type ChatLanguage = 'auto' | 'en' | 'de';

const CHAT_LANGUAGES: Array<{ value: ChatLanguage; labelKey: string }> = [
  { value: 'auto', labelKey: 'settings.personalization.chatLanguageAuto' },
  { value: 'en', labelKey: 'settings.personalization.chatLanguageEn' },
  { value: 'de', labelKey: 'settings.personalization.chatLanguageDe' },
];

export function PersonalizationPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const seedTone =
    session.status === 'authenticated' ? (session.session.toneVerbosity ?? 'balanced') : 'balanced';
  const seedInstructions =
    session.status === 'authenticated' ? (session.session.customInstructions ?? '') : '';
  const seedChatLanguage =
    session.status === 'authenticated' ? (session.session.chatLanguage ?? 'auto') : 'auto';

  const [tone, setTone] = useState<Tone>(seedTone);
  const [instructions, setInstructions] = useState<string>(seedInstructions);
  const [chatLanguage, setChatLanguage] = useState<ChatLanguage>(seedChatLanguage);
  const [savedFlash, setSavedFlash] = useState(false);

  // Keep local form state in sync with the session after a refetch (e.g.
  // after sign-in or after a parallel save from another tab).
  useEffect(() => {
    if (session.status !== 'authenticated') return;
    setTone(session.session.toneVerbosity ?? 'balanced');
    setInstructions(session.session.customInstructions ?? '');
    setChatLanguage(session.session.chatLanguage ?? 'auto');
  }, [session]);

  const saveTone = useMutation({
    mutationFn: async (next: Tone) => {
      const { error } = await api.PATCH('/account/profile', {
        body: { toneVerbosity: next },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });

  const saveChatLanguage = useMutation({
    mutationFn: async (next: ChatLanguage) => {
      const { error } = await api.PATCH('/account/profile', {
        body: { chatLanguage: next },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });

  const saveInstructions = useMutation({
    mutationFn: async (next: string) => {
      const trimmed = next.trim();
      const { error } = await api.PATCH('/account/profile', {
        body: { customInstructions: trimmed.length > 0 ? trimmed : null },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    },
  });

  return (
    <div className="flex flex-col gap-4" data-testid="settings-panel-personalization">
      <Card>
        <CardContent className="flex flex-col gap-4 py-6">
          <Label className="text-sm font-medium">
            {t('settings.personalization.chatLanguage')}
          </Label>
          <Select
            value={chatLanguage}
            onValueChange={(v) => {
              const next = v as ChatLanguage;
              setChatLanguage(next);
              saveChatLanguage.mutate(next);
            }}
          >
            <SelectTrigger className="w-full" data-testid="settings-chat-language-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CHAT_LANGUAGES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('settings.personalization.chatLanguageHint')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4 py-6">
          <Label className="text-sm font-medium">{t('settings.personalization.tone')}</Label>
          <Select
            value={tone}
            onValueChange={(v) => {
              const next = v as Tone;
              setTone(next);
              saveTone.mutate(next);
            }}
          >
            <SelectTrigger className="w-full" data-testid="settings-tone-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TONES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('settings.personalization.toneHint')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          <Label className="text-sm font-medium">
            {t('settings.personalization.customInstructions')}
          </Label>
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value.slice(0, MAX_INSTRUCTIONS))}
            placeholder={t('settings.personalization.customInstructionsPlaceholder')}
            rows={6}
            data-testid="settings-custom-instructions"
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('settings.personalization.customInstructionsHint')}</span>
            <span>
              {instructions.length} / {MAX_INSTRUCTIONS}
            </span>
          </div>
          <div className="flex items-center justify-end gap-3">
            {savedFlash ? (
              <span className="text-xs text-emerald-600">
                {t('settings.personalization.saved')}
              </span>
            ) : null}
            <Button
              type="button"
              size="sm"
              onClick={() => saveInstructions.mutate(instructions)}
              disabled={saveInstructions.isPending || instructions === seedInstructions}
              data-testid="settings-save-instructions"
            >
              {saveInstructions.isPending
                ? t('settings.personalization.saving')
                : t('settings.personalization.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('settings.personalization.memory')}</Label>
            <Badge variant="outline">{t('settings.comingSoon')}</Badge>
          </div>
          <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-muted-foreground">
            <Brain className="size-8 opacity-50" />
            <p>{t('settings.personalization.memoryEmpty')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
